import * as React from "react";
import shortid from "shortid";
import cloneDeep from "lodash/cloneDeep";
import { Modal } from "@lib/components";
import { ModalContent } from "@lib/components";
import { Button } from "@lib/components";
import { Tag } from "@lib/components";
import { FormTreeSelect } from "@lib/components";
import { RotateLoader } from "@lib/components";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import { arrayMove } from "react-sortable-hoc";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components";
import { RestaurantMobileFormOptionSet } from "../menus/forms/mobileForms/mobileOptionSet";
import { RestaurantFormOptionSet } from "../menus/forms/option_set";
import { MenuListButton } from "../menus/layout/button";
import { MenuListItem } from "../menus/layout/item";
import { MenuList } from "../menus/layout/list";
import { MenuListItemV2 } from "./item-v2";
import { RestaurantFormOptionSetV2 } from "./option_set_v2";
import { TabGroup, TabItem } from "../settings-v2/receipt-printing";
import {
	buildModifierAttachUpdates,
	buildModifierDetachUpdates,
	getItemIdsWithModifiers,
	normalizeObject,
	RestaurantUtils,
} from "@lib/common";
import { UI } from "../../../../../core/ui";
import { isObject } from "lodash";
import { MenuItemText } from "../menus/layout/floating-button";
import { MenuMoreAction } from "../../../../components/v2/menumoreaction";
import { MenuActionContainer, MenuActionItem } from "../../../../components/v2/layout.c";
import { BulkConfirmModal } from "../../../../components/v2/bulk-confirm-modal";
import {
	applyMenuListView,
	MenuListSortDirection,
	sortByName,
} from "../menus/utils/sortLists";
import { saveOriginalOptionSetOrder } from "../menus/menuApiOriginal";

/** API may omit `type: 'pizza'` on modifiers whose options already use point pricing; form needs `type` for column layout. */
function normalizeV2ModifierFormValues(item: any): any {
	if (!item || typeof item !== "object") return item;
	if (item.type === "pizza" || item.using_points === true) return item;
	const opts = item.options;
	if (!Array.isArray(opts) || opts.length === 0) return item;
	const hasPointOption = opts.some((opt: any) => {
		const p = opt?.price;
		if (!p || typeof p !== "object") return false;
		if (p.type === "point") return true;
		const v = p.value;
		if (v != null && typeof v === "object" && "whole" in v && "half" in v) return true;
		if (p.pointValue != null && typeof p.pointValue === "object" && "whole" in p.pointValue)
			return true;
		return false;
	});
	if (!hasPointOption) return item;
	return { ...item, type: "pizza" };
}

interface Props {
	filterText?: string;
	sortDirection?: MenuListSortDirection;
	onSortDirectionChange?: (direction: MenuListSortDirection) => void;
}
type BulkAction = "delete" | "hide" | "available" | "attach" | "detach";
interface State {
	reorder: boolean;
	index_copy: number | null;
	index_panel: number | null;
	index_remove: number | null;

	loading_copy: boolean;
	loading_remove: boolean;
	loading_reorder: boolean;
	loading_list_sort: boolean;

	loading_optionsets: boolean;
	selected_ids: string[];
	selected_loading: boolean;
	bulk_action: BulkAction | null;
	attach_selected_items: string[];
}

@inject("store")
@observer
export class RestaurantOptionSetListV2 extends MobxComponent<Props, State> {
	backup: T.Schema.Restaurant.RestaurantSchema["option_sets"] | null;
	backup_v2: T.Schema.Restaurant.RestaurantSchema["option_sets_v2"] | null = null;

	constructor(props: Props) {
		super(props);
		this.state = {
			reorder: false,
			index_copy: null,
			index_panel: null,
			index_remove: null,
			loading_copy: false,
			loading_remove: false,
			loading_reorder: false,
			loading_list_sort: false,
			loading_optionsets: true,
			selected_ids: [],
			selected_loading: false,
			bulk_action: null,
			attach_selected_items: [],
		};
		this.backup = null;
	}


	async componentDidMount() {
		const { store } = this.injected;
		const restaurant = store.restaurant!;
		const get_menus = await store.api.menu_get_menus(restaurant._id!);
    const options = get_menus.options || get_menus.option_sets.map((o: any) => o.options).flat() || [];
    const optionById = new Map(options.map((o: any) => [o.id, o]));

    const optionSets = get_menus.modifiers || get_menus.option_sets;
    optionSets.forEach((modifier: any) => {
      if (Array.isArray(modifier.options) && modifier.options.length) {
        modifier.options = modifier.options
          .map((id: string) => optionById.get(id) ?? null)
          .filter(Boolean);
      }
    });
		store.updateRestaurant({
			option_sets_v2: get_menus.modifiers,
      menus_v2: get_menus.menus || []
    });
		this.setState({
			loading_optionsets: false
		})
	}


	componentWillUnmount() {
		if (this.state.reorder) {
			this.reorder_cancel();
		}
	}

	create_item = (): T.Schema.Restaurant.Menu.RestaurantOptionSet => {
    const { store } = this.injected;
		return {
			_id: shortid.generate(),
			name: "",
			display_name: "",
			show_in_menu: false,
			conditions: {
				required: false,
				multi_select: false,
				quantity_select: false,
				min_options: 0,
				max_options: 0,
				free_amount: 0,
			},
			options: [],
      ...(store.isNewMenuSchema ? {
				type: 'standard',
				_internal: { disabled: false },
			} : {})
		};
	};

	getOptionSetId = (optionSet: any) => optionSet.id || optionSet._id;

	getDisplayOptionSetsV2 = () => {
		const r = this.injected.store.restaurant!;
		const { reorder } = this.state;
		const list = r.option_sets_v2 || [];
		if (reorder) return list;
		return applyMenuListView(list, {
			filterText: this.props.filterText,
			sortDirection: this.props.sortDirection ?? null,
		});
	};

	getDisplayOptionSets = () => {
		const r = this.injected.store.restaurant!;
		const { reorder } = this.state;
		const list = r.option_sets || [];
		if (reorder) return list;
		return applyMenuListView(list, {
			filterText: this.props.filterText,
			sortDirection: this.props.sortDirection ?? null,
		});
	};

	cancelListSort = () => {
		this.props.onSortDirectionChange?.(null);
	};

	saveListSort = async () => {
		const direction = this.props.sortDirection;
		if (!direction) return;
		const { store } = this.injected;
		const r = store.restaurant!;
		this.setState({ loading_list_sort: true });
		try {
			if (store.isNewMenuSchema) {
				const sorted = sortByName(r.option_sets_v2 || [], direction);
				store.updateRestaurant({ option_sets_v2: sorted });
				const order = sorted
					.map((optionSet: any) => this.getOptionSetId(optionSet))
					.filter(Boolean);
				if (order.length > 0) {
					await store.api.modifiers_order({
						restaurant_id: r._id!,
						order,
					});
				}
			} else {
				const sorted = sortByName(r.option_sets || [], direction);
				store.updateRestaurant({ option_sets: sorted });
				await saveOriginalOptionSetOrder(
					store,
					sorted.map((optionSet) => optionSet._id)
				);
			}
			UI.notification.success("Option sets sorted");
			this.props.onSortDirectionChange?.(null);
		} catch (e: any) {
			UI.notification.error(e?.message ?? "Failed to save sort order");
		}
		this.setState({ loading_list_sort: false });
	};

	listSortSaveCancel = () => {
		const { loading_list_sort } = this.state;
		return (
			<div className="grid-2 xs xs-gap m-t-10">
				<div className="col">
					<MenuListButton onClick={this.saveListSort}>
						{loading_list_sort && <RotateLoader size={2} color="white" />}
						{!loading_list_sort && "Save"}
					</MenuListButton>
				</div>
				<div className="col">
					<MenuListButton onClick={this.cancelListSort}>Cancel</MenuListButton>
				</div>
			</div>
		);
	};

	list_item = () => {
		const { store } = this.injected;
		const { activeMobileMenu } = store
		const { reorder, loading_reorder, loading_optionsets} = this.state;
		const pendingListSort = !reorder && !!this.props.sortDirection;
		const r = this.injected.store.restaurant!;
		const option_sets = this.getDisplayOptionSets();
		if(loading_optionsets) return <RotateLoader size={4} />
		return (
			<>
				{(activeMobileMenu.name != 'create-option-set' && activeMobileMenu.name != 'edit-option-set') && <><MenuList id="option-set-list" type="OPTIONSET">
					{option_sets.map((item, i) => {
						const originalIndex = (r.option_sets || []).findIndex(
							(optionSet) => optionSet._id === item._id
						);
						return (
							<MenuListItemV2
								key={item._id || 'option-set-'+i}
								type="OPTIONSET"
								id={item._id || 'option-set-'+i}
								mode={reorder ? "reOrder" : "normal"}
								name={item.name +
									(item.using_points === true ? " 🍕" : "")}
								index={reorder ? i : originalIndex}
								onEdit={() => {
									store.setActiveMobileMenu('edit-option-set', 'menus', originalIndex);
									this.setState({ index_panel: store.activeMobileMenu?.data || originalIndex });
								}}
								onRemove={() => this.modal_remove_open(originalIndex)}
								onCopy={() => this.modal_copy_open(originalIndex)} />
						);
					})}
					</MenuList>
					{pendingListSort && this.listSortSaveCancel()}
					{!pendingListSort && (
					<TabGroup className='menus menus-end menus-header' style={{ overflow: 'unset', marginBottom: '50px' }}>
							<TabItem className="footer-button" onClick={() => { store.setActiveMobileMenu('create-option-set', 'menus', -1);}} style={{ height: '42px'}}>
								Create New Option Set
							</TabItem>
							<TabItem className="footer-button" key="btn-arrange" onClick={this.reorder_start} style={{ height: '42px'}}>
								Re-Arrange
							</TabItem>
						</TabGroup>)}
					</>

					}

				{reorder && (
					<>
						<div className="grid-2 xs xs-gap m-t-10">
							<div className="col">
								<MenuListButton onClick={this.reorder_save}>
									{loading_reorder && (
										<RotateLoader size={2} color="white" />
									)}
									{!loading_reorder && "Save"}
								</MenuListButton>
							</div>
							<div className="col">
								<MenuListButton onClick={this.reorder_cancel}>
									Cancel
								</MenuListButton>
							</div>
						</div>
						<div className="text-center m-t-3">
							<p>
								Click + hold to drag and drop items to re-order
								them
							</p>
						</div>
					</>
				)}
			</>
		);
	};

	list_item_v2 = () => {
		const { store } = this.injected;
		const { activeMobileMenu } = store
		const { reorder, loading_reorder, selected_ids } = this.state;
		const pendingListSort = !reorder && !!this.props.sortDirection;
		const r = this.injected.store.restaurant!;
		const optionSetsV2 = this.getDisplayOptionSetsV2();
		return (
			<>
				{(activeMobileMenu.name != 'create-option-set' && activeMobileMenu.name != 'edit-option-set') && <>
					<MenuList id="option-set-list" type="OPTIONSET">
					{optionSetsV2.map((item, i) => {
						const id = this.getOptionSetId(item) || `option-set-${i}`;
						const originalIndex = (r.option_sets_v2 || []).findIndex(
							(optionSet: any) => this.getOptionSetId(optionSet) === id
						);
						return (
							<MenuListItemV2
								key={id}
								type="OPTIONSET"
								id={id}
								mode={reorder ? "reOrder" : "normal"}
								name={
									<div className="flex-l-r-center width100">
										<MenuItemText className="m-l-3" style={{maxWidth: '40vw'}}>
											{item.name +
												//@ts-ignore
												(item.using_points === true ? " 🍕" : "")}
										</MenuItemText>
										{item._internal?.disabled === true && <Tag className="m-r-2">Hidden</Tag>}
									</div>
								}
								index={reorder ? i : originalIndex}
								onEdit={() => {
									store.setActiveMobileMenu('edit-option-set', 'menus', originalIndex);
									this.setState({ index_panel: store.activeMobileMenu?.data || originalIndex });
								}}
								onRemove={() => this.modal_remove_open(originalIndex)}
								onCopy={() => this.modal_copy_open(originalIndex)}
								selectable={!reorder}
								selected={selected_ids.indexOf(id) !== -1}
								onToggleSelect={this.toggle_selected_option_set}
							/>
						);
					})}
					</MenuList>
					{pendingListSort && this.listSortSaveCancel()}
					{!reorder && !pendingListSort && (
					<TabGroup className='menus menus-end menus-header' style={{ overflow: 'unset', marginBottom: '50px' }}>
							<TabItem className="footer-button" onClick={() => { store.setActiveMobileMenu('create-option-set', 'menus', -1);}} style={{ height: '42px'}}>
								Create New Option Set
							</TabItem>
							<TabItem className="footer-button" key="btn-arrange" onClick={this.reorder_start} style={{ height: '42px'}}>
								Re-Arrange
							</TabItem>
						</TabGroup>)}
					</>

					}

				{reorder && (
					<>
						<div className="grid-2 xs xs-gap m-t-10">
							<div className="col">
								<MenuListButton onClick={this.reorder_save}>
									{loading_reorder && (
										<RotateLoader size={2} color="white" />
									)}
									{!loading_reorder && "Save"}
								</MenuListButton>
							</div>
							<div className="col">
								<MenuListButton onClick={this.reorder_cancel}>
									Cancel
								</MenuListButton>
							</div>
						</div>
						<div className="text-center m-t-3">
							<p>
								Click + hold to drag and drop items to re-order
								them
							</p>
						</div>
					</>
				)}
			</>
		);
	};
	panel_set = (index_panel: number | null) => {
		this.setState({ index_panel });
	};

	panel_create = () => {
		const { store } = this.injected;
		const { activeMobileMenu } = store
		const r = this.injected.store.restaurant!;
		let initialValues = null;
		if ( activeMobileMenu.data !== null) {
			if ( activeMobileMenu.data === -1) {
				initialValues = this.create_item();
			} else {
				let item;

				if(store.isNewMenuSchema) {
					item = (r.option_sets_v2 || [])[ activeMobileMenu.data];
					initialValues = normalizeV2ModifierFormValues(cloneDeep(item));
				} else {
					item = r.option_sets[ activeMobileMenu.data];
					initialValues = cloneDeep(item);
				}
			}
		}

		return (
			<>
				{ (activeMobileMenu.name == 'create-option-set' || activeMobileMenu.name == 'edit-option-set') && <RestaurantFormOptionSetV2
					type={activeMobileMenu.data === -1 ? "create" : "edit"}
					initialValues={initialValues}
					close={() => {}}
				/>}
				</>

		);
	};

	copy = async (index: number) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		if(store.isNewMenuSchema) {
			const optionSetsV2 = r.option_sets_v2 || [];
			const targetOptionSet = optionSetsV2[index];
			const option_set = cloneDeep(targetOptionSet);
			//@ts-ignore
			option_set.name = `${option_set.name} - Copy`;
			this.setState({ loading_copy: true })
			try {
				const resOptionSet = await store.api.menu_create_modifier({
					restaurant_id: r._id,
					data: option_set
				})
				UI.notification.success("Option set copied")
				optionSetsV2.push(resOptionSet)
					store.updateRestaurant({
					option_sets_v2: optionSetsV2
				})
			} catch(e) {
				UI.notification.error(e.error)
			}
			this.modal_copy_close()

		} else {
			await this.saveRestaurant({
				successMsg: "Tag copied",
				process: (r) => {
					const option_set = cloneDeep(r.option_sets[index]);
					option_set._id = shortid.generate();
					option_set.name = `${option_set.name} - Copy`;
					for (const [i] of option_set.options.entries()) {
						option_set.options[i]._id = shortid.generate();
					}
					r.option_sets.splice(index + 1, 0, option_set);
					return {
						update: { $set: { option_sets: r.option_sets } },
					};
				},
				before: () => this.setState({ loading_copy: true }),
				onSuccess: () => this.modal_copy_close(),
				onFail: () => this.setState({ loading_copy: false }),
				onError: () => this.setState({ loading_copy: false }),
			});
		}
	};
	remove = async (index: number) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		if(store.isNewMenuSchema) {
			const optionSetsV2 = r.option_sets_v2 || [];
			const id = optionSetsV2[index].id
			this.setState({ loading_remove: true })
			try {
				await store.api.menu_delete_modifier({
					restaurant_id: r._id,
					modifier_id: id
				})
				UI.notification.success("Option set deleted");

				store.updateRestaurant({
					option_sets_v2: optionSetsV2.filter(o => o.id !== id)
				})

				store.setActiveMobileMenu(
					store.activeMobileMenu.referrer.pop()!,
					Array.from(new Set(store.activeMobileMenu.referrer))
				);
			} catch(e) {
				UI.notification.error(e.error)
			}
			this.modal_remove_close()

		} else {
			await this.saveRestaurant({
				successMsg: "Option set deleted",
				process: (r) => {
					const _id = r.option_sets[index]._id;

					r.menus = cloneDeep(r.menus);

					for (const [i, m] of r.menus.entries()) {
						for (const [k, c] of m.categories.entries()) {
							for (const [z, d] of c.dishes.entries()) {
								const found = d.option_sets.indexOf(_id);
								if (found !== -1) {
									r.menus[i].categories[k].dishes[
										z
									].option_sets.splice(found, 1);
								}
							}
						}
					}

					const { store } = this.injected;
					store.api
						.menu_stock_update({
							_id: store.restaurant!._id,
							items: r.option_sets[index].options.map((o) => ({
								_id: o._id,
								key: "option_set_options",
								value: "",
							})),
						})
						.then((result) => {
							store.setRestaurantStock(result.stock);
						})
						.catch(console.log);

					r.option_sets.splice(index, 1);

					return {
						update: {
							$set: { option_sets: r.option_sets, menus: r.menus },
						},
					};
				},
				before: () => this.setState({ loading_remove: true }),
				onSuccess: () => {
					this.modal_remove_close();
				},
				onFail: () => this.setState({ loading_remove: false }),
				onError: () => this.setState({ loading_remove: false }),
			});
		}
	};

	refreshV2MenusFromApi = async () => {
		const { store } = this.injected;
		const restaurant = store.restaurant!;
		const get_menus = await store.api.menu_get_menus(restaurant._id!);
		const options = get_menus.options || get_menus.option_sets?.map((o: any) => o.options).flat() || [];
		const optionById = new Map(options.map((o: any) => [o.id, o]));
		const optionSets = get_menus.modifiers || get_menus.option_sets || [];
		optionSets.forEach((modifier: any) => {
			if (Array.isArray(modifier.options) && modifier.options.length) {
				modifier.options = modifier.options
					.map((id: string) => optionById.get(id) ?? null)
					.filter(Boolean);
			}
		});
		store.updateRestaurant({
			option_sets_v2: optionSets,
			menus_v2: get_menus.menus || [],
		});
	};

	toggle_selected_option_set = (optionSetId: string) => {
		const selected_ids = [...this.state.selected_ids];
		const index = selected_ids.indexOf(optionSetId);
		if (index === -1) selected_ids.push(optionSetId);
		else selected_ids.splice(index, 1);
		this.setState({ selected_ids });
	};

	select_all = () => {
		this.setState({
			selected_ids: this.getDisplayOptionSetsV2()
				.map((optionSet: any) => this.getOptionSetId(optionSet))
				.filter(Boolean),
		});
	};

	clear_selection = () => {
		this.setState({ selected_ids: [] });
	};

	open_bulk_action = (bulk_action: BulkAction) => {
		const restaurant = this.injected.store.restaurant!;
		const attach_selected_items =
			bulk_action === "detach"
				? getItemIdsWithModifiers(restaurant.menus_v2 || [], this.state.selected_ids)
				: [];
		this.setState({ bulk_action, attach_selected_items });
	};

	close_bulk_action = () => {
		this.setState({
			bulk_action: null,
			selected_loading: false,
			attach_selected_items: [],
			selected_ids: [],
		});
	};

	patchV2SelectedOptionSets = async (patchInternal: Record<string, unknown>) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const optionSetsV2 = r.option_sets_v2 || [];
		for (const id of this.state.selected_ids) {
			const current = optionSetsV2.find((optionSet: any) => this.getOptionSetId(optionSet) === id);
			const res = await store.api.menu_partial_update_modifier({
				restaurant_id: r._id,
				modifier_id: id,
				data: {
					_internal: {
						...(current as any)?._internal,
						...patchInternal,
					},
				},
			});
			if (res?.error) throw res;
		}
		await this.refreshV2MenusFromApi();
	};

	bulkDeleteV2OptionSets = async () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		for (const id of this.state.selected_ids) {
			const res = await store.api.menu_delete_modifier({
				restaurant_id: r._id,
				modifier_id: id,
			});
			if (res?.error) throw res;
		}
		await this.refreshV2MenusFromApi();
	};

	bulkAttachV2OptionSets = async () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const itemsToUpdate = buildModifierAttachUpdates(
			r.menus_v2 || [],
			this.state.selected_ids,
			this.state.attach_selected_items
		);
		if (itemsToUpdate.length > 0) {
			const res = await store.api.menu_partial_update_batch_items({
				restaurant_id: r._id,
				data: { items: itemsToUpdate },
			});
			if (res?.error) throw res;
		}
		await this.refreshV2MenusFromApi();
	};

	bulkDetachV2OptionSets = async () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const itemsToUpdate = buildModifierDetachUpdates(
			r.menus_v2 || [],
			this.state.selected_ids,
			this.state.attach_selected_items
		);
		if (itemsToUpdate.length > 0) {
			const res = await store.api.menu_partial_update_batch_items({
				restaurant_id: r._id,
				data: { items: itemsToUpdate },
			});
			if (res?.error) throw res;
		}
		await this.refreshV2MenusFromApi();
	};

	apply_bulk_action = async () => {
		const { bulk_action, selected_ids } = this.state;
		if (!bulk_action || selected_ids.length === 0) return;
		this.setState({ selected_loading: true });
		try {
			if (bulk_action === "hide") await this.patchV2SelectedOptionSets({ disabled: true });
			if (bulk_action === "available") await this.patchV2SelectedOptionSets({ disabled: false });
			if (bulk_action === "delete") await this.bulkDeleteV2OptionSets();
			if (bulk_action === "attach") await this.bulkAttachV2OptionSets();
			if (bulk_action === "detach") await this.bulkDetachV2OptionSets();
			UI.notification.success(this.bulk_action_success_message(bulk_action));
			this.setState({
				selected_ids: [],
				selected_loading: false,
				bulk_action: null,
				attach_selected_items: [],
			});
		} catch (e: any) {
			UI.notification.error(e?.error ?? e?.message ?? "Failed to apply bulk action");
			this.setState({ selected_loading: false });
		}
	};

	bulk_action_title = (bulk_action: BulkAction) => {
		const count = this.state.selected_ids.length;
		if (bulk_action === "delete") return `Delete ${count} option sets`;
		if (bulk_action === "hide") return `Hide ${count} option sets`;
		if (bulk_action === "available") return `Make ${count} option sets available`;
		if (bulk_action === "attach") return `Attach ${count} option sets`;
		return `Detach ${count} option sets`;
	};

	bulk_action_description = (bulk_action: BulkAction) => {
		if (bulk_action === "delete") return "This will permanently delete the selected option sets.";
		if (bulk_action === "hide") return "Hidden option sets will not show on the store or in item selection.";
		if (bulk_action === "available") return "Available option sets will show on the store and in item selection.";
		if (bulk_action === "attach") return "Choose the items these selected option sets should be attached to.";
		return "Choose the items these selected option sets should be detached from.";
	};

	bulk_action_success_message = (bulk_action: BulkAction) => {
		if (bulk_action === "delete") return "Option sets deleted";
		if (bulk_action === "hide") return "Option sets hidden";
		if (bulk_action === "available") return "Option sets available";
		if (bulk_action === "attach") return "Option sets attached";
		return "Option sets detached";
	};

	modal_copy_open = (index: number | null) => {
		this.setState({
			index_copy: index,
			loading_copy: false,
		});
	};
	modal_copy_close = () => {
		this.setState({
			index_copy: null,
			loading_copy: false,
		});
	};
	modal_remove_open = (index: number | null) => {
		this.setState({
			index_remove: index,
			loading_remove: false,
		});
	};
	modal_remove_close = () => {
		this.setState({
			index_remove: null,
			loading_remove: false,
		});
	};

	modal_copy = () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const { index_copy, loading_copy } = this.state;

		let item;
		let fn;
		if (index_copy !== null) {
			item = r.option_sets[index_copy];
			if(store.isNewMenuSchema) {
				item = (r.option_sets_v2 || [])[index_copy]
			}
			fn = () => this.copy(index_copy);
		}
		if(!item) return

		return (
			<Modal
				width="sm"
				close={this.modal_copy_close}
				closeButton={false}
				active={!!item}
			>
				<ModalContent className="flex-l-r-center">
					<h4 className="">Copy Option Set</h4>
					<p className="big underline">{
						//@ts-ignore
					item && item.name
					}</p>
				</ModalContent>
				<ModalContent className="flex-right">
					<Button
						type="button"
						className="m-r-2 width100 max100px"
						onClick={this.modal_copy_close}
					>
						Cancel
					</Button>
					<Button
						type="button"
						className="width100 max100px"
						color="primary"
						onClick={fn}
					>
						{loading_copy && (
							<RotateLoader size={2} color="white" />
						)}
						{!loading_copy && "Copy"}
					</Button>
				</ModalContent>
			</Modal>
		);
	};
	modal_remove = () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const { index_remove, loading_remove } = this.state;

		let item;
		let fn;
		if (index_remove !== null) {
			item = r.option_sets[index_remove];
			if(store.isNewMenuSchema) {
				item = (r.option_sets_v2 || [])[index_remove]
			}
			fn = () => this.remove(index_remove);
		}
		if(!item) return
		return (
			<Modal
				width="sm"
				close={this.modal_remove_close}
				closeButton={false}
				active={!!item}
			>
				<ModalContent className="flex-l-r-center">
					<h4 className="">Delete Option Set</h4>
					<p className="big underline">{
					//@ts-ignore
					item && item.name
					}</p>
				</ModalContent>
				<ModalContent className="flex-right">
					<Button
						type="button"
						className="m-r-2 width100 max100px"
						onClick={this.modal_remove_close}
					>
						Cancel
					</Button>
					<Button
						type="button"
						className="width100 max100px"
						color="primary"
						onClick={fn}
					>
						{loading_remove && (
							<RotateLoader size={2} color="white" />
						)}
						{!loading_remove && "Delete"}
					</Button>
				</ModalContent>
			</Modal>
		);
	};

	bulk_action_modal = () => {
		const { bulk_action, selected_loading, attach_selected_items } = this.state;
		const restaurant = this.injected.store.restaurant!;
		if (!bulk_action) return null;
		const isItemPicker = bulk_action === "attach" || bulk_action === "detach";

		return (
			<BulkConfirmModal
				active={true}
				title={this.bulk_action_title(bulk_action)}
				description={this.bulk_action_description(bulk_action)}
				loading={selected_loading}
				confirmDisabled={isItemPicker && attach_selected_items.length === 0}
				onClose={this.close_bulk_action}
				onConfirm={this.apply_bulk_action}
			>
				{isItemPicker && (
					<div style={{ width: "100%", maxHeight: 240, overflowY: "auto" }}>
						<FormTreeSelect
							id="bulk-attach-option-sets-v2"
							selected={attach_selected_items}
							nodes={(RestaurantUtils.menu as any).getTreeFilterV2(restaurant)}
							onChange={(itemIds) => this.setState({ attach_selected_items: itemIds })}
						/>
					</div>
				)}
			</BulkConfirmModal>
		);
	};

	bulk_action_bar = () => {
		const { selected_ids, selected_loading } = this.state;
		return (
			<MenuMoreAction
				show={true}
				title="More Actions"
				onHide={this.clear_selection}
				onSave={() => {}}
				itemCount={selected_ids.length}
			>
				<MenuActionContainer>
					<MenuActionItem onClick={this.select_all}>Select All</MenuActionItem>
					<MenuActionItem onClick={() => this.open_bulk_action("available")}>Available</MenuActionItem>
					<MenuActionItem onClick={() => this.open_bulk_action("hide")}>Hide</MenuActionItem>
					<MenuActionItem onClick={() => this.open_bulk_action("attach")}>Attach</MenuActionItem>
					<MenuActionItem onClick={() => this.open_bulk_action("detach")}>Detach</MenuActionItem>
					<MenuActionItem onClick={() => this.open_bulk_action("delete")}>Delete</MenuActionItem>
					{selected_loading && (
						<div className="text-center p-1">
							<RotateLoader size={2} />
						</div>
					)}
				</MenuActionContainer>
			</MenuMoreAction>
		);
	};

	reorder_start = () => {
		this.setState({ reorder: true });
		const r = this.injected.store.restaurant!;
		if (this.injected.store.isNewMenuSchema) {
			const v2 = r.option_sets_v2 ?? [];
			this.backup_v2 = cloneDeep(v2);
			this.backup = null;
		} else {
			this.backup = cloneDeep(r.option_sets);
			this.backup_v2 = null;
		}
	};

	reorder_cancel = () => {
		const store = this.injected.store;
		if (store.isNewMenuSchema && this.backup_v2) {
			store.updateRestaurant({ option_sets_v2: this.backup_v2 });
			this.backup_v2 = null;
		} else if (this.backup) {
			store.updateRestaurant({ option_sets: this.backup });
		}
		this.setState({ reorder: false });
		this.backup = null;
	};

	reorder_save = async () => {
		const r = this.injected.store.restaurant!;
		const store = this.injected.store;

		if (store.isNewMenuSchema) {
			const list = r.option_sets_v2 ?? [];
			this.setState({ loading_reorder: true });
			try {
				const order = list.map((o: any) => o.id).filter(Boolean);
				if (order.length > 0) {
					await store.api.modifiers_order({
						restaurant_id: r._id!,
						order,
					});
				}
				UI.notification.success('Option sets re-arranged');
				this.setState({ reorder: false, loading_reorder: false });
				this.backup_v2 = null;
			} catch (e: any) {
				UI.notification.error(e?.message ?? 'Failed to save order');
				this.setState({ loading_reorder: false });
			}
			return;
		}

		const backup = cloneDeep(r);
		if (this.backup) backup.option_sets = this.backup;
		await this.saveRestaurantSilent({
			backup: backup,
			update: { $set: { option_sets: r.option_sets } },
			successMsg: "Option sets re-arranged",
			before: () => this.setState({ loading_reorder: true }),
			onSuccess: () => {
				this.setState({ reorder: false, loading_reorder: false });
				this.backup = null;
			},
			onFail: () => this.setState({ loading_reorder: false }),
			onError: () => this.setState({ loading_reorder: false }),
		});
	};

	onDragEnd = (result: DropResult) => {
		const { destination, source } = result;
		if (!destination || destination.index === source.index) return;
		const store = this.injected.store;
		const r = store.restaurant!;

		if (store.isNewMenuSchema) {
			const current = r.option_sets_v2 ?? [];
			if (source.index < 0 || source.index >= current.length || destination.index < 0 || destination.index >= current.length) return;
			const next = arrayMove(current, source.index, destination.index);
			store.updateRestaurant({ option_sets_v2: next });
			return;
		}

		const current = r.option_sets ?? [];
		if (source.index < 0 || source.index >= current.length || destination.index < 0 || destination.index >= current.length) return;
		const next = arrayMove(current, source.index, destination.index);
		store.updateRestaurant({ option_sets: next });
	};

	render() {
		const { store } = this.injected
		const { selected_ids, bulk_action } = this.state;
		const listViewKey = `${this.props.filterText ?? ""}:${this.props.sortDirection ?? ""}`;
		return (
			<div className="relative">
				{selected_ids.length > 0 && !bulk_action && this.bulk_action_bar()}
				<DragDropContext key={listViewKey} onDragEnd={this.onDragEnd}>
					{!store.isNewMenuSchema &&  this.list_item()}
					{store.isNewMenuSchema &&  this.list_item_v2()}
					{this.panel_create()}
					{this.modal_copy()}
					{this.modal_remove()}
					{this.bulk_action_modal()}
				</DragDropContext>
			</div>
		);
	}
}
